import { useEffect, useState } from "react";
import api, { apiError } from "../../../lib/api";
import { toast } from "sonner";

let _rowKeySeq = 0;
const _nextRowKey = () => `rk_${++_rowKeySeq}`;
const _withRowKeys = (data) => {
  if (!data) return data;
  const out = { ...data };
  for (const list of ["stats", "paper", "late"]) {
    if (Array.isArray(out[list])) out[list] = out[list].map((row) => ({ ...row, _k: _nextRowKey() }));
  }
  return out;
};

export function ReturnsEditor() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/returns").then((r) => setD(_withRowKeys(r.data))).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return <p className="t-small text-muted-s">Loading...</p>;
  const set = (k, v) => setD({ ...d, [k]: v });
  const setRow = (list, i, key, v) => { const arr = [...d[list]]; arr[i] = { ...arr[i], [key]: v }; set(list, arr); };
  const addRow = (list, tpl) => set(list, [...(d[list] || []), { ...tpl, _k: _nextRowKey() }]);
  const delRow = (list, i) => set(list, d[list].filter((_, j) => j !== i));
  const save = async () => {
    try {
      const strip = (arr) => (arr || []).map((row) => { const c = { ...row }; delete c._k; return c; });
      const payload = {
        ...d,
        stats: strip(d.stats),
        late: strip(d.late),
        paper: strip(d.paper).map((r) => ({ ...r, multiple: Number(r.multiple) || 0 })),
      };
      await api.put("/admin/returns", payload);
      toast.success("Returns section saved. It is now live on the homepage.");
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="max-w-3xl space-y-8" data-testid="admin-returns-editor">
      <p className="t-small text-muted-s">Edit the public landing "AI venture returns" section. Changes go live on the homepage immediately.</p>
      <div className="space-y-3">
        <span className="t-label">Headline</span>
        <input className="input-solera" value={d.headline || ""} onChange={(e) => set("headline", e.target.value)} data-testid="returns-headline" />
        <span className="t-label">Intro</span>
        <textarea className="input-solera" rows={3} value={d.intro || ""} onChange={(e) => set("intro", e.target.value)} data-testid="returns-intro" />
        <div className="flex gap-3 items-center">
          <span className="t-cap text-muted-s w-24">As at</span>
          <input className="input-solera" value={d.as_at || ""} onChange={(e) => set("as_at", e.target.value)} data-testid="returns-asat" />
        </div>
      </div>
      <div className="space-y-2">
        <span className="t-label">Stat cards</span>
        {(d.stats || []).map((s, i) => (
          <div key={s._k} className="grid grid-cols-[1fr_1.2fr_2fr_auto] gap-2 items-center" data-testid={`returns-stat-row-${i}`}>
            <input className="input-solera" value={s.value} onChange={(e) => setRow("stats", i, "value", e.target.value)} placeholder="value" />
            <input className="input-solera" value={s.label} onChange={(e) => setRow("stats", i, "label", e.target.value)} placeholder="label" />
            <input className="input-solera" value={s.desc} onChange={(e) => setRow("stats", i, "desc", e.target.value)} placeholder="description" />
            <button className="t-label hover:!text-[#E8E6E1]" onClick={() => delRow("stats", i)}>Remove</button>
          </div>
        ))}
        <button className="t-label hover:!text-[#E8E6E1]" onClick={() => addRow("stats", { value: "", label: "", desc: "" })} data-testid="returns-add-stat">Add stat</button>
      </div>
      <div className="space-y-2">
        <span className="t-label">Early multiples (bars)</span>
        {(d.paper || []).map((r, i) => (
          <div key={r._k} className="grid grid-cols-[1.2fr_0.8fr_0.7fr_2fr_auto] gap-2 items-center" data-testid={`returns-paper-row-${i}`}>
            <input className="input-solera" value={r.name} onChange={(e) => setRow("paper", i, "name", e.target.value)} placeholder="name" />
            <input className="input-solera" value={r.label} onChange={(e) => setRow("paper", i, "label", e.target.value)} placeholder="label" />
            <input className="input-solera fig" value={r.multiple} onChange={(e) => setRow("paper", i, "multiple", e.target.value)} placeholder="mult" />
            <input className="input-solera" value={r.sub} onChange={(e) => setRow("paper", i, "sub", e.target.value)} placeholder="detail" />
            <button className="t-label hover:!text-[#E8E6E1]" onClick={() => delRow("paper", i)}>Remove</button>
          </div>
        ))}
        <button className="t-label hover:!text-[#E8E6E1]" onClick={() => addRow("paper", { name: "", label: "", multiple: 100, sub: "" })} data-testid="returns-add-paper">Add row</button>
      </div>
      <div className="space-y-2">
        <span className="t-label">Late stage rows</span>
        {(d.late || []).map((r, i) => (
          <div key={r._k} className="grid grid-cols-[1.5fr_0.8fr_1.5fr_auto] gap-2 items-center" data-testid={`returns-late-row-${i}`}>
            <input className="input-solera" value={r.name} onChange={(e) => setRow("late", i, "name", e.target.value)} placeholder="name" />
            <input className="input-solera" value={r.label} onChange={(e) => setRow("late", i, "label", e.target.value)} placeholder="label" />
            <input className="input-solera" value={r.sub} onChange={(e) => setRow("late", i, "sub", e.target.value)} placeholder="detail" />
            <button className="t-label hover:!text-[#E8E6E1]" onClick={() => delRow("late", i)}>Remove</button>
          </div>
        ))}
        <button className="t-label hover:!text-[#E8E6E1]" onClick={() => addRow("late", { name: "", label: "", sub: "" })} data-testid="returns-add-late">Add row</button>
      </div>
      <div className="space-y-2">
        <span className="t-label">Access statement</span>
        <textarea className="input-solera" rows={3} value={d.access_text || ""} onChange={(e) => set("access_text", e.target.value)} data-testid="returns-access-text" />
        <input className="input-solera" value={(d.access_names || []).join(", ")} onChange={(e) => set("access_names", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} placeholder="SpaceX, OpenAI, ..." data-testid="returns-access-names" />
      </div>
      <button className="btn-primary" onClick={save} data-testid="returns-save">Save returns section</button>
    </div>
  );
}
